import { useLayoutEffect, useRef, useState } from 'react';

import { IconChevronDown } from '@tabler/icons-react';
import { NavLink, useLocation } from 'react-router-dom';
import { FloatingFocusManager, FloatingPortal, usePopout } from 'ui-kit/hooks/usePopout';

import { isTabActive, SubHeaderTab, useSubHeaderTabs } from './useSubHeaderTabs';
import { SubHeaderMenuItem } from '../SubHeaderMenuItem/SubHeaderMenuItem';

const MORE_BUTTON_CLASSES =
  'flex items-center gap-1 whitespace-nowrap border-b-2 px-6 py-[10px] text-xsm transition-colors duration-200';

export function countTabsThatFit(
  tabWidths: number[],
  moreButtonWidth: number,
  availableWidth: number,
): number {
  const total = tabWidths.reduce((sum, width) => sum + width, 0);
  if (total <= availableWidth) return tabWidths.length;

  let used = moreButtonWidth;
  let count = 0;
  for (const width of tabWidths) {
    if (used + width > availableWidth) break;
    used += width;
    count += 1;
  }
  return count;
}

function MoreTabs({ tabs }: { tabs: SubHeaderTab[] }) {
  const { pathname } = useLocation();
  const popout = usePopout({ placement: 'bottom-end', offset: 0, role: 'dialog' });
  const holdsActiveTab = tabs.some((tab) => isTabActive(tab, pathname));

  return (
    <li className="flex list-none items-center self-end">
      <button
        type="button"
        ref={popout.refs.setReference}
        {...popout.getReferenceProps()}
        className={`${MORE_BUTTON_CLASSES} ${
          holdsActiveTab
            ? 'border-accent-default text-foreground-default light:text-black'
            : 'border-transparent-0 text-gray-500 hover:text-white light:hover:text-black'
        }`}
      >
        More ({tabs.length})
        <IconChevronDown size={14} />
      </button>
      {popout.isOpen && (
        <FloatingPortal>
          <FloatingFocusManager context={popout.context} modal={false}>
            <div
              ref={popout.refs.setFloating}
              style={popout.floatingStyles}
              {...popout.getFloatingProps()}
              aria-label="More pages"
              className="z-40 min-w-[200px] rounded-md bg-gray-950 py-1 text-white shadow-[0px_8px_16px_0px_rgba(13,13,13,0.9)]"
            >
              <ul className="m-0 p-0">
                {tabs.map((tab) => (
                  <li key={tab.key} className="list-none">
                    <NavLink
                      to={tab.to}
                      onClick={() => popout.setIsOpen(false)}
                      className={`text-xsm block whitespace-nowrap border-l-2 px-4 py-2.5 no-underline ${
                        tab.disabled
                          ? 'pointer-events-none border-transparent-0 text-gray-400'
                          : isTabActive(tab, pathname)
                            ? 'border-accent-default text-foreground-default'
                            : 'border-transparent-0 text-gray-400 hover:text-white'
                      }`}
                    >
                      {tab.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </li>
  );
}

function OverflowTabs({ tabs }: { tabs: SubHeaderTab[] }) {
  const rowRef = useRef<HTMLUListElement>(null);
  const measureRef = useRef<HTMLUListElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabs.length);
  const tabCount = tabs.length;
  const tabsKey = tabs.map((tab) => `${tab.key}:${tab.label}`).join('|');

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    if (!row || !measure || typeof ResizeObserver === 'undefined') {
      setVisibleCount(tabCount);
      return;
    }

    const update = () => {
      const items = Array.from(measure.children) as HTMLElement[];
      const tabWidths = items.slice(0, tabCount).map((item) => item.offsetWidth);
      const moreButtonWidth = items[tabCount]?.offsetWidth ?? 0;
      setVisibleCount(countTabsThatFit(tabWidths, moreButtonWidth, row.clientWidth));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(row);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [tabsKey, tabCount]);

  const visible = tabs.slice(0, visibleCount);
  const hidden = tabs.slice(visibleCount);

  return (
    <div className="relative min-w-0 flex-1">
      <ul ref={rowRef} className="m-0 flex h-full min-w-0 p-0">
        {visible.map((tab) => (
          <SubHeaderMenuItem key={tab.key} tab={tab} />
        ))}
        {hidden.length > 0 && <MoreTabs tabs={hidden} />}
      </ul>
      <ul
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 m-0 flex p-0"
      >
        {tabs.map((tab) => (
          <li key={tab.key} className="list-none">
            <span className="text-xsm block whitespace-nowrap border-b-2 px-6 py-[10px]">
              {tab.label}
            </span>
          </li>
        ))}
        <li className="list-none">
          <span className={MORE_BUTTON_CLASSES}>
            More ({tabs.length})
            <IconChevronDown size={14} />
          </span>
        </li>
      </ul>
    </div>
  );
}

export function SubHeader() {
  const subHeader = useSubHeaderTabs();
  if (!subHeader) return null;

  return (
    <div className="flex min-h-[50px] border-t border-slate-500 bg-background-surface pl-2 light:border-slate-100 light:bg-white light:text-gray-600 md:pl-[max(186px,calc(100%/6_-_54px))]">
      <OverflowTabs tabs={subHeader.tabs} />
    </div>
  );
}
